import React, { useState, useRef, useCallback, useMemo } from 'react';
import styles from './bcg-textual-alert-popup.module.scss';

export default function BCGTagInputBox({
  value = [],
  onChange,
  placeholder = 'Enter name',
  disabled = false,
  tagType = 'brand', // or 'competitor'
  showClearAll = false,
  onClearAll,
  minHeight = '40px',
  onInputChange = () => {},
}) {
  const [input, setInput] = useState('');
  const inputRef = useRef();

  const addTag = useCallback((tag) => {
    if (!tag || value.includes(tag)) return;
    onChange([...value, tag]);
  }, [onChange, value]);

  const handleInputChange = useCallback((e) => {
    setInput(e.target.value);
    onInputChange(e.target.value);
  }, [onInputChange]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && input.trim()) {
      addTag(input.trim());
      setInput('');
      e.preventDefault();
    } else if (e.key === 'Backspace' && !input && value.length) {
      onChange(value.slice(0, -1));
    }
  }, [addTag, input, onChange, value]);

  const handlePaste = useCallback((e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text');
    const tags = pasted
      .split(/[\n,;\t]+/)
      .map((t) => t.trim())
      .filter((t) => t && !value.includes(t));
    if (tags.length) {
      onChange([...value, ...tags]);
      setInput('');
    }
  }, [onChange, value]);

  const removeTag = useCallback((idx) => {
    onChange(value.filter((_, i) => i !== idx));
  }, [onChange, value]);

  const tagElements = useMemo(() => (
    value.map((tag, idx) => (
      <span
        key={tag + idx}
        className={`${styles['bcg-tag-input-box__tag']} ${tagType === 'brand'
          ? styles['bcg-tag-input-box__tag--brand']
          : styles['bcg-tag-input-box__tag--competitor']
        }`}
      >
        {tag}
        <span
          className={styles['bcg-tag-input-box__remove']}
          onClick={() => removeTag(idx)}
          tabIndex={0}
          role="button"
          aria-label="Remove"
        >
          &#x2715;
        </span>
      </span>
    ))
  ), [value, tagType, removeTag, styles]);

  return (
    <section 
      className={`${styles['bcg-tag-input-box__container']} ${disabled ? styles['disabled'] : ''}`}
      style={{
        minHeight: minHeight,
      }}
    >
      <div className={`${styles['bcg-tag-input-box']}`}>
      {tagElements}
      <input
        ref={inputRef}
        className={styles['bcg-tag-input-box__input'] + ' border-none'}
        value={input}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        placeholder={value.length === 0 ? placeholder : ''}
        disabled={disabled}
      />
      {showClearAll && (
        <button
          type="button"
          className={styles['bcg-tag-input-box__clear']}
          onClick={onClearAll}
          tabIndex={0}
        >
          Clear All
        </button>
      )}
    </div>
    </section>
  );
} 